import { chartNames, deviceKitName, strCode } from "../../consts";
import { t } from "../../services";
import { arrayUtil, deviceUtil, locationUtil, rumEventsUtil } from "../../utils";

// Page loads
// _________________________________________________________________________
const getPageLoads = (data) => {
  const metricsConfig = rumEventsUtil.getMetricsConfig({
    chartName: chartNames.PAGE_LOADS
  });

  const chartData = rumEventsUtil.prepareChartData({
    data
  });

  if (!chartData?.grouped || !chartData?.dates) {
    return 0;
  }

  const { grouped, dates } = chartData;

  let result = [];
  let totalPageLoadsCount = 0;
  const totalDurationsList = [];

  grouped?.size > 0 &&
    dates.forEach((day) => {
      const events = grouped.get(day);

      let pageLoadsCount = 0;
      let avgDuration = 0;

      if (events && events?.length > 0) {
        pageLoadsCount = events.length;
        totalPageLoadsCount = totalPageLoadsCount + events.length;

        const durationsList = [];

        events.forEach((event) => {
          if (event?.eventDetails?.duration !== undefined) {
            durationsList.push(event.eventDetails.duration);
          }
        });

        avgDuration =
          durationsList && durationsList?.length ? arrayUtil.getAverageValue(durationsList) : 0;

        durationsList && durationsList?.length && totalDurationsList.push(...durationsList);
      }

      result.push({
        date: day,
        metrics: {
          [metricsConfig.count.id]: pageLoadsCount,
          [metricsConfig.duration.id]: avgDuration
        }
      });
    });

  const totalAvgDuration = totalDurationsList?.length
    ? arrayUtil.getAverageValue(totalDurationsList)
    : undefined;
  totalPageLoadsCount = totalPageLoadsCount > 0 ? totalPageLoadsCount : undefined;

  return { result, totalPageLoads: totalPageLoadsCount, totalDuration: totalAvgDuration };
};

// Device Connections
// _________________________________________________________________________
const getConnectionTime = (data, devices) => {
  const metricsConfig = rumEventsUtil.getMetricsConfig({
    chartName: chartNames.CONNECTION_TIME
  });

  const chartData = rumEventsUtil.prepareChartData({
    data,
    devices
  });

  if (!chartData?.grouped || !chartData?.dates || !metricsConfig) {
    return [];
  }

  const { grouped, dates } = chartData;

  const result = [];

  const hasGroups = {
    monoDevices: false,
    duoDevices: false
  };

  grouped?.size > 0 &&
    dates.forEach((day) => {
      const events = grouped.get(day);

      let avgSpeedMono = 0;
      let avgSpeedDuo = 0;

      if (events && events?.length > 0) {
        const durationLists = {
          monoDevices: [],
          duoDevices: []
        };

        events.forEach((event) => {
          let device = event?.eventDetails?.deviceName;

          const group = deviceUtil.getDeviceGroupByName(device);

          if (group && event?.eventDetails?.duration !== undefined) {
            durationLists[group].push(event.eventDetails.duration);
            hasGroups[group] = true;
          }
        });

        avgSpeedMono = durationLists.monoDevices?.length
          ? arrayUtil.getAverageValue(durationLists.monoDevices)
          : 0;

        avgSpeedDuo = durationLists.duoDevices?.length
          ? arrayUtil.getAverageValue(durationLists.duoDevices)
          : 0;
      }

      result.push({
        date: day,
        metrics: {
          [metricsConfig.mono.id]: avgSpeedMono,
          [metricsConfig.duo.id]: avgSpeedDuo
        }
      });
    });

  return rumEventsUtil.filterResults({ result, devices, hasGroups, metricsConfig });
};

const getDeviceUsage = (data, devices) => {
  const metricsConfig = rumEventsUtil.getMetricsConfig({
    chartName: chartNames.IQOS_DEVICE_USAGE
  });

  const chartData = rumEventsUtil.prepareChartData({
    data,
    devices,
    groupFn: (event) => event?.eventDetails?.deviceName
  });

  if (!chartData?.grouped || !chartData?.dates || !metricsConfig) {
    return [];
  }

  const { grouped } = chartData;
  const deviceNames = Object.keys(deviceKitName).map((key) => deviceKitName[key]);

  const result = [];

  const hasGroups = {
    monoDevices: false,
    duoDevices: false
  };

  grouped?.size > 0 &&
    deviceNames.forEach((device) => {
      const events = grouped.get(device);

      let countMono = 0;
      let countDuo = 0;

      if (events && events?.length > 0) {
        const usageCount = {
          monoDevices: 0,
          duoDevices: 0
        };

        events.forEach((event) => {
          const group = deviceUtil.getDeviceGroupByName(device);

          if (group && event?.eventDetails?.duration !== undefined) {
            usageCount[group] += 1;
            hasGroups[group] = true;
          }
        });

        countMono = usageCount.monoDevices;
        countDuo = usageCount.duoDevices;
      }

      if (countMono || countDuo) {
        result.push({
          device,
          metrics: {
            [metricsConfig.mono.id]: countMono > 0 ? countMono : null,
            [metricsConfig.duo.id]: countDuo > 0 ? countDuo : null
          }
        });
      }
    });

  return rumEventsUtil.filterResults({
    result,
    agregatorName: "device",
    devices,
    hasGroups,
    metricsConfig
  });
};

const getDeviceConnectionsCountByMarket = (data, devices) => {
  const metricsConfig = rumEventsUtil.getMetricsConfig({
    chartName: chartNames.IQOS_DEVICE_CONNECTIONS_COUNT
  });

  const chartData = rumEventsUtil.prepareChartData({
    data,
    devices,
    groupFn: (event) => event.metadata?.market
  });

  if (!chartData?.grouped || !metricsConfig) {
    return [];
  }

  const { grouped } = chartData;
  let result = [];
  let totalDeviceConnectionsCount = 0;

  grouped.size > 0 &&
    grouped.forEach((events, market) => {
      let count = 0;

      const platformCounts = {
        Android: 0,
        Windows: 0,
        Linux: 0,
        "Mac OS": 0
      };

      events.forEach((event) => {
        const osName = event?.metadata?.osName;
        if (osName in platformCounts) {
          platformCounts[osName]++;
        }
      });

      if (events && events?.length > 0) {
        count = events?.length;
      }

      totalDeviceConnectionsCount += count;

      result.push({
        market,
        metrics: {
          [metricsConfig.windows.id]: platformCounts["Windows"],
          [metricsConfig.android.id]: platformCounts["Android"],
          [metricsConfig.linux.id]: platformCounts["Linux"],
          [metricsConfig.macos.id]: platformCounts["Mac OS"]
        }
      });
    });

  return { result, totalDeviceConnectionsCount };
};

const getDeviceConnectionsCountByApp = (data, devices) => {
  const metricsConfig = rumEventsUtil.getMetricsConfig({
    chartName: chartNames.IQOS_DEVICE_CONNECTIONS_COUNT
  });

  const chartData = rumEventsUtil.prepareChartData({
    data,
    devices,
    groupFn: (event) => event.appType
  });

  if (!chartData?.grouped || !metricsConfig) {
    return [];
  }

  const { grouped } = chartData;
  let result = [];
  let totalDeviceConnectionsCount = 0;

  grouped.size > 0 &&
    grouped.forEach((events, appType) => {
      let count = 0;

      const platformCounts = {
        Android: 0,
        Windows: 0,
        Linux: 0,
        "Mac OS": 0
      };

      events.forEach((event) => {
        const osName = event?.metadata?.osName;
        if (osName in platformCounts) {
          platformCounts[osName]++;
        }
      });

      if (events && events?.length > 0) {
        count = events?.length;
      }

      totalDeviceConnectionsCount += count;

      result.push({
        appType,
        metrics: {
          [metricsConfig.windows.id]: platformCounts["Windows"],
          [metricsConfig.android.id]: platformCounts["Android"],
          [metricsConfig.linux.id]: platformCounts["Linux"],
          [metricsConfig.macos.id]: platformCounts["Mac OS"]
        }
      });
    });

  return { result, totalDeviceConnectionsCount };
};

const getDeviceConnectionsCountByLocation = (data, devices) => {
  const metricsConfig = rumEventsUtil.getMetricsConfig({
    chartName: chartNames.IQOS_DEVICE_CONNECTIONS_COUNT
  });

  const chartData = rumEventsUtil.prepareChartData({
    data,
    devices,
    groupFn: (event) => event.metadata?.store
  });

  if (!chartData?.grouped || !metricsConfig) {
    return [];
  }

  const { grouped } = chartData;

  const result = [];
  let totalDeviceConnectionsCount = 0;
  grouped.size > 0 &&
    grouped.forEach((events, location) => {
      // location can be "null" (string) and it should be handled like a null
      const locationName =
        location && location !== "null"
          ? locationUtil.getLocationNameById(location)
          : t(strCode.NO_LOCATION);
      const platformCounts = {
        Android: 0,
        Windows: 0,
        Linux: 0,
        "Mac OS": 0
      };

      events.forEach((event) => {
        const osName = event?.metadata?.osName;
        if (osName in platformCounts) {
          platformCounts[osName]++;
        }
      });

      if (locationName) {
        let count = 0;

        if (events && events?.length > 0) {
          count = events?.length;
        }

        totalDeviceConnectionsCount += count;

        result.push({
          location: locationName,
          metrics: {
            [metricsConfig.windows.id]: platformCounts["Windows"],
            [metricsConfig.android.id]: platformCounts["Android"],
            [metricsConfig.linux.id]: platformCounts["Linux"],
            [metricsConfig.macos.id]: platformCounts["Mac OS"]
          }
        });
      }
    });

  return { result, totalDeviceConnectionsCount };
};

const overviewDashboardUtil = {
  // Page loads
  // _________________________________________________________________________
  getPageLoads,

  // Device Connections
  // _________________________________________________________________________
  getConnectionTime,
  getDeviceUsage,
  getDeviceConnectionsCountByMarket,
  getDeviceConnectionsCountByLocation,
  getDeviceConnectionsCountByApp
};

export default overviewDashboardUtil;
